import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { ReportType } from '@/app/constants/reportType'
import { OutputType } from '@/app/constants/outputType'
import { ActiveMenuItem } from '@/app/constants/menuItem'
import { RolePermission } from '@/modules/shared/types/account'
import { useUser } from '@/modules/shared/context/UserContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { getDifferenceBetweenDatesInDays } from '@/app/common/dateHelper'
import {
  FaIcon,
  faUsers,
  faPrint,
  faChevronDown,
  faChevronUp,
  faHeart,
  faPaperPlaneSolid,
  faPaperPlaneRegular,
  faMagnifyingGlassSolid,
  faXmark,
  faCircleCheck,
} from '@/lib/fa'
import {
  getReportNumberByType,
  getReportTypeByNumber,
} from '@/app/services/printout/reportTypeResolver'
import usePrintOutService from '../services/PrintOutService'
import {
  useReportCapabilities,
  supportsOutputFormat,
} from '../hooks/useReportCapabilities'
import { EtBrandSygnet } from '@/modules/shared/components/svg/EtBrandSygnet'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { Checkbox } from '@/ui/form/primitives/checkbox'
import { TextField } from '@/ui/form/controls/TextField'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import ReportParamsPanel from './ReportParamsPanel'
import { useReportParamsStore } from '../stores/reportParamsStore'
import { useQueryCompanies } from '@/modules/shared/query-client/useQueryCompanies'
import { isServiceCompany, isUnderServiceAccount } from '@/app/constants/companyType'

enum ReportGroupType {
  DataCorrectnessControl = 0,
  ItdControlAndAnalysis = 1,
  PipControl = 2,
  StatementsAndForms = 3,
  WorkTrips = 4,
  VehicleDataCorrectnessControl = 5,
  ManagerReports = 6,
  DataAccessEvents = 7,
  ServiceReports = 8,
}

type ReportItem = {
  name: string
  type: ReportType
  disabled: boolean
}

/**
 * Definicja pozycji na liscie. Nie niesie `disabled` - dostepnosc w wybranym formacie
 * wynika z capabilities serwera (flagi modelu dokumentu), a nie z tabelki w kliencie.
 */
type ReportDefinition = Omit<ReportItem, 'disabled'>

type PrintoutGroupDefinition = {
  group: string
  type: ReportGroupType
  reports: ReportDefinition[]
}

type PrintoutGroup = {
  group: string
  type: ReportGroupType
  reports: ReportItem[]
}

type ListType = 'favorites' | 'shipping'

/**
 * Raporty wylaczone z zaznaczania wielokrotnego - parytet z WPF
 * (PrintoutData z canMultiSelect: false w PrintoutDataViewModel).
 */
const NON_MULTI_SELECT_REPORTS: ReadonlySet<ReportType> = new Set([
  ReportType.DATA_ACCESS_EVENTS,
  ReportType.FOREIGN_COUNTRY_SERVICE_DECLARATIONS_DEADLINES,
  ReportType.CARD_READING_DEADLINE_SERVICE,
])

/** Raporty liczone dla całej firmy lub konta usługowego, bez zaznaczania kierowców. */
const NO_SELECTION_GROUPS: ReadonlySet<ReportGroupType> = new Set([
  ReportGroupType.DataAccessEvents,
  ReportGroupType.ServiceReports,
])
const NO_SELECTION_REPORTS: ReadonlySet<ReportType> = new Set([ReportType.DRIVER_ACTIVENESS])

const allowsNoSelection = (report: ReportItem, groupType: ReportGroupType) =>
  NO_SELECTION_GROUPS.has(groupType) || NO_SELECTION_REPORTS.has(report.type)

/** Opoznienie filtrowania listy raportow po wpisaniu frazy. */
const SEARCH_DEBOUNCE_MS = 300

const EMPTY_GENERATED_REPORTS: ReadonlyArray<ReportType> = []

/** Tlo podswietlenia trafienia - ten sam zolty, co w tabeli kierowcow. */
const SEARCH_HIGHLIGHT_CLASS = 'tw-bg-[#FFF47B]'

/**
 * Pojedynczy znak bez ogonkow i malymi literami. "ł" nie rozklada sie przez NFD,
 * wiec mapuje je recznie - inaczej "zaladunek" nie trafiloby w "załadunek".
 *
 * Normalizacja idzie znak po znaku, bo na tej samej funkcji opiera sie
 * podswietlanie: musi umiec przelozyc pozycje w znormalizowanym tekscie
 * na pozycje w oryginale.
 */
const normalizeChar = (char: string): string =>
  char
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/ł/g, 'l')


const normalizeForSearch = (value: string): string =>
  Array.from(value).map(normalizeChar).join('').trim()


const buildSearchIndex = (text: string) => {
  let normalized = ''
  const offsets: number[] = []
  let offset = 0

  for (const char of text) {
    const chunk = normalizeChar(char)
    for (let i = 0; i < chunk.length; i++) {
      normalized += chunk[i]
      offsets.push(offset)
    }
    offset += char.length
  }
  offsets.push(text.length)

  return { normalized, offsets }
}


const highlightSearchMatch = (
  text: string,
  normalizedQuery: string,
): React.ReactNode => {
  if (!normalizedQuery) return text

  const { normalized, offsets } = buildSearchIndex(text)
  const parts: React.ReactNode[] = []
  let cursor = 0
  let searchFrom = 0

  for (;;) {
    const found = normalized.indexOf(normalizedQuery, searchFrom)
    if (found === -1) break

    const start = offsets[found]
    const end = offsets[found + normalizedQuery.length]

    if (start > cursor) parts.push(text.slice(cursor, start))
    parts.push(
      <span key={start} className={SEARCH_HIGHLIGHT_CLASS}>
        {text.slice(start, end)}
      </span>,
    )

    cursor = end
    searchFrom = found + normalizedQuery.length
  }

  if (parts.length === 0) return text
  if (cursor < text.length) parts.push(text.slice(cursor))

  return <>{parts}</>
}

type Props = {
  activeItem: ActiveMenuItem
  outputFormat: OutputType
  dateFrom: Moment
  dateTo: Moment
  checkedDrivers: string[]
  checkedVehicles: string[]
  companyId: string
  onReportClick: (report: ReportItem) => void
  onDateRangeWarning: () => void
  showOnlyFavorites?: boolean
  listType?: ListType
  /** Zaznaczone raporty - przekazanie wlacza tryb wielokrotnego wyboru. */
  selectedReports?: ReadonlySet<ReportType>
  onToggleReport?: (report: ReportItem) => void
  onToggleReports?: (reports: ReportItem[], select: boolean) => void
  generatedReports?: ReadonlyArray<ReportType>
  lastGeneratedReports?: ReadonlyArray<ReportType>
}

const PrintoutGroupsList: React.FC<Props> = ({
  activeItem,
  outputFormat,
  dateFrom,
  dateTo,
  checkedDrivers,
  checkedVehicles,
  companyId,
  onReportClick,
  onDateRangeWarning,
  showOnlyFavorites = false,
  listType = 'favorites',
  selectedReports,
  onToggleReport,
  onToggleReports,
  generatedReports = EMPTY_GENERATED_REPORTS,
  lastGeneratedReports = EMPTY_GENERATED_REPORTS,
}) => {
  const isSelectable = selectedReports != null && onToggleReport != null
  const generatedReportTypes = React.useMemo(
    () => new Set(generatedReports),
    [generatedReports],
  )
  const lastGeneratedReportTypes = React.useMemo(
    () => new Set(lastGeneratedReports),
    [lastGeneratedReports],
  )
  const { t } = useLocalize()
  const { getRolesInCompany, isDriver, isAdmin, isCompanyAdmin, isPaymentAdmin, isDemoCompanyAdmin } = useUser()
  const { basicDrivers } = useDrivers()
  const permissions = getRolesInCompany(companyId)
  const { data: companies } = useQueryCompanies({ includeDeleted: false })
  // Raporty usługowe zbierają dane z firm konta usługowego, w których użytkownik może drukować.
  const currentCompany = companies?.find((c) => String(c.id) === String(companyId))
  const showServiceReports =
    isAdmin() || isServiceCompany(currentCompany) || isUnderServiceAccount(currentCompany)

  const singleSelectedDriver = React.useMemo(() => {
    if (
      activeItem === ActiveMenuItem.PRINTOUT_DRIVER &&
      checkedDrivers.length === 1
    ) {
      const id = parseInt(checkedDrivers[0])
      return basicDrivers.find((d) => d.id === id) ?? null
    }
    return null
  }, [activeItem, checkedDrivers, basicDrivers])

  const [expandedCategories, setExpandedCategories] = React.useState<
    Set<number>
  >(new Set())
  const [favorites, setFavorites] = React.useState<Set<ReportType>>(new Set())
  const [shipping, setShipping] = React.useState<Set<ReportType>>(new Set())
  const [isLoading, setIsLoading] = React.useState(true)
  const [searchInput, setSearchInput] = React.useState('')
  const [openedReport, setOpenedReport] = React.useState<ReportType | null>(
    null,
  )
  const syncParamsCompany = useReportParamsStore((s) => s.syncCompany)

  React.useEffect(() => {
    syncParamsCompany(companyId)
    setOpenedReport(null)
  }, [companyId, syncParamsCompany])

  // Opcje odznaczonego raportu nie trafią do wydruku, więc panel się zwija.
  React.useEffect(() => {
    if (openedReport != null && !selectedReports?.has(openedReport)) {
      setOpenedReport(null)
    }
  }, [openedReport, selectedReports])

  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS)
  const normalizedSearch = React.useMemo(
    () => normalizeForSearch(debouncedSearch),
    [debouncedSearch],
  )
  const isSearching = normalizedSearch.length > 0

  const capabilities = useReportCapabilities(companyId)

  const {
    addPrintOutFav,
    deletePrintOutFav,
    getFavPrintOuts,
    addPrintOutShipping,
    deletePrintOutShipping,
    getShippingPrintOuts,
  } = usePrintOutService()

  const buildSet = (reports: any): Set<ReportType> => {
    const set = new Set<ReportType>()
    if (Array.isArray(reports)) {
      reports.forEach((item: { reportType: number }) => {
        const reportType = getReportTypeByNumber(item.reportType)
        if (reportType !== ReportType.NONE) {
          set.add(reportType)
        }
      })
    }
    return set
  }

  React.useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const [favReports, shippingReports] = await Promise.all([
          getFavPrintOuts({ companyId }),
          getShippingPrintOuts({ companyId }),
        ])
        if (cancelled) return
        setFavorites(buildSet(favReports))
        setShipping(buildSet(shippingReports))
      } catch (error) {
        console.error('Błąd podczas ładowania list wydruków:', error)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [companyId])

  const toggleCategory = (index: number) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(index)) {
        newSet.delete(index)
      } else {
        newSet.add(index)
      }
      return newSet
    })
  }

  const toggleFavorite = (reportType: ReportType, e: React.MouseEvent) => {
    e.stopPropagation()
    setFavorites((prev) => {
      const newSet = new Set(prev)
      const id = getReportNumberByType(reportType)
      if (newSet.has(reportType)) {
        newSet.delete(reportType)
        deletePrintOutFav({ companyId, reportId: id })
      } else {
        addPrintOutFav({ companyId, reportId: id })
        newSet.add(reportType)
      }
      return newSet
    })
  }

  const toggleShipping = (reportType: ReportType, e: React.MouseEvent) => {
    e.stopPropagation()
    setShipping((prev) => {
      const newSet = new Set(prev)
      const id = getReportNumberByType(reportType)
      if (newSet.has(reportType)) {
        newSet.delete(reportType)
        deletePrintOutShipping({ companyId, reportId: id })
      } else {
        addPrintOutShipping({ companyId, reportId: id })
        newSet.add(reportType)
      }
      return newSet
    })
  }

  const activeSet = listType === 'shipping' ? shipping : favorites

  const getPrintoutGroup = (group: number): PrintoutGroupDefinition[] => {
    switch (group) {
      case 0:
        return [
          {
            group: 'printout.dataCorrectnessControl',
            type: ReportGroupType.DataCorrectnessControl,
            reports: [
              {
                name: 'printout.driverDataCompletness',
                type: ReportType.DRIVER_DATA_COMPLETNESS,
              },
              {
                name: 'printout.driverAttestationReport',
                type: ReportType.DRIVER_ATTESTATION_REPORT,
              },
              {
                name: 'printout.cardReadingDeadline',
                type: ReportType.CARD_READING_DEADLINE,
              },
              {
                name: 'printout.driverCards',
                type: ReportType.DRIVER_CARDS,
              },
              {
                name: 'printout.driverCardReading',
                type: ReportType.DRIVER_CARD_READING,
              },
              {
                name: 'printout.documentValidityDeadline',
                type: ReportType.DOCUMENT_VALIDITY_DEADLINE,
              },
              {
                name: 'printout.driverFaultsAndEvent',
                type: ReportType.DRIVER_FAULTS_AND_EVENTS,
              },
              {
                name: 'printout.virtualDisplacement',
                type: ReportType.VIRTUAL_DISPLACEMENT,
              },
              {
                name: 'printout.driverCardInsertionWithdrawal',
                type: ReportType.DRIVER_CARD_INSERTION_WITHDRAWAL,
              },
              {
                name: 'printout.driverCardInsertionWithdrawalSimple',
                type: ReportType.DRIVER_CARD_INSERTION_WITHDRAWAL_SIMPLE,
              },
              // Jak w desktopie: raport aktywności tylko dla administratora.
              ...(isAdmin()
                ? [{ name: 'printout.driverActiveness', type: ReportType.DRIVER_ACTIVENESS }]
                : []),
            ],
          },
          {
            group: 'printout.itdControlAndAnalysis',
            type: ReportGroupType.ItdControlAndAnalysis,
            reports: [
              {
                name: 'printout.violations',
                type: ReportType.VIOLATIONS,
              },
              {
                name: 'printout.groupedViolations',
                type: ReportType.GROUPED_VIOLATIONS,
              },
              {
                name: 'printout.driverActivities',
                type: ReportType.DRIVER_ACTIVITIES,
              },
              {
                name: 'printout.driverActivitySummary',
                type: ReportType.DRIVER_ACTIVITY_SUMMARY,
              },
              {
                name: 'printout.localization',
                type: ReportType.LOCALIZATION,
              },
              {
                name: 'printout.driverDayDetails',
                type: ReportType.DRIVER_DAY_DETAILS,
              },
              {
                name: 'printout.driverDayDetailsWithSignatureReport',
                type: ReportType.DRIVER_DAY_DETAILS_WITH_SIGNATURE_REPORT,
              },
              {
                name: 'printout.driverPeriodDistance',
                type: ReportType.DRIVER_PERIOD_DISTANCE,
              },
              {
                name: 'printout.weeklyRests',
                type: ReportType.WEEKLY_RESTS,
              },
              {
                name: 'printout.weeklyRestsWithLocation',
                type: ReportType.WEEKLY_RESTS_WITH_LOCATION,
              },
              {
                name: 'printout.timesheetTimeSummary',
                type: ReportType.TIMESHEET_TIME_SUMMARY,
              },
              {
                name: 'printout.multipleDriverYearlyActivities',
                type: ReportType.DRIVER_CALENDAR,
              },
              {
                name: 'printout.driverAvailableTime',
                type: ReportType.DRIVER_AVAILABLE_TIME,
              },
              {
                name: 'printout.companyReputationIndex',
                type: ReportType.COMPANY_REPUTATION_INDEX,
              },
            ],
          },
        ]

      case 1:
        return [
          {
            group: 'printout.pipControl',
            type: ReportGroupType.PipControl,
            reports: [
              {
                name: 'printout.settingsInformationCard',
                type: ReportType.SETTINGS_INFORMATION_CARD,
              },
              {
                name: 'printout.timesheet',
                type: ReportType.TIMESHEET,
              },
              {
                name: 'printout.timesheetDetailedWageReport',
                type: ReportType.TIMESHEET_DETAILED_WAGE_REPORT,
              },
              {
                name: 'printout.timesheetPaymentDetails',
                type: ReportType.TIMESHEET_PAYMENT_DETAILS,
              },
              {
                name: 'printout.timesheetPaymentReport',
                type: ReportType.TIMESHEET_PAYMENT_REPORT,
              },
              {
                name: 'printout.timesheetApplicationForOverhoursForDrivers',
                type: ReportType.TIMESHEET_APPLICATION_FOR_OVERHOURS_FOR_DRIVERS,
              },
              {
                name: 'printout.timesheetPeriodSummary',
                type: ReportType.TIMESHEET_PERIOD_SUMMARY,
              },
              {
                name: 'printout.timesheetApplicationForLeave',
                type: ReportType.TIMESHEET_APPLICATION_FOR_LEAVE_FOR_DRIVERS,
              },
              {
                name: 'printout.timesheetRewardNotification',
                type: ReportType.TIMESHEET_REWARD_FOR_DRIVERS,
              },
              {
                name: 'printout.driverContractsFromPeriod',
                type: ReportType.DRIVER_CONTRACTS_FROM_PERIOD,
              },
              {
                name: 'printout.timesheetEmptyDays',
                type: ReportType.TIMESHEET_EMPTY_DAYS,
              },
              {
                name: 'printout.timesheetSummary',
                type: ReportType.TIMESHEET_SUMMARY,
              },
              {
                name: 'printout.timesheetYearSummary',
                type: ReportType.TIMESHEET_YEAR_SUMMARY,
              },
              {
                name: 'printout.driverYearGrossAmounts',
                type: ReportType.DRIVER_YEAR_GROSS_AMOUNTS,
              },
              {
                name: 'printout.timesheetAmountsReport',
                type: ReportType.TIMESHEET_AMOUNTS_REPORT,
              },
              {
                name: 'printout.timesheetForeignAmountSummary',
                type: ReportType.TIMESHEET_FOREIGN_AMOUNT_SUMMARY,
              },
              {
                name: 'printout.timesheetForeignAmountsReport',
                type: ReportType.TIMESHEET_FOREIGN_AMOUNTS_REPORT,
              },
              {
                name: 'printout.timesheetForeignAmountsNewVersionReport',
                type: ReportType.TIMESHEET_FOREIGN_AMOUNTS_NEW_VERSION_REPORT,
              },
              {
                name: 'printout.timesheetLumpSumSettlement',
                type: ReportType.TIMESHEET_LUMPSUM_SETTLEMENT,
              },
              {
                name: 'printout.timesheetLumpSumUtilization',
                type: ReportType.TIMESHEET_LUMPSUM_UTILIZATION,
              },
              {
                name: 'printout.timesheetLumpSumSettlementSummary',
                type: ReportType.TIMESHEET_LUMPSUM_SETTLEMENT_SUMMARY,
              },
              {
                name: 'printout.timesheetWorkingHoursReport',
                type: ReportType.TIMESHEET_WORKING_HOURS,
              },
              {
                name: 'printout.driverFreeDays',
                type: ReportType.DRIVER_FREE_DAYS,
              },
              {
                name: 'printout.timesheetLeaveDaysReport',
                type: ReportType.TIMESHEET_LEAVE_DAYS_REPORT,
              },
              {
                name: 'printout.timesheetOverhoursSummary',
                type: ReportType.TIMESHEET_OVERHOURS_SUMMARY,
              },
              {
                name: 'printout.mobilityPackagePayrollReport',
                type: ReportType.MOBILITY_PACKAGE_PAYROL_REPORT,
              },
              {
                name: 'printout.rewardsToAcceptReport',
                type: ReportType.REWARDS_TO_ACCEPT_REPORT,
              },
              {
                name: 'printout.tripCosts',
                type: ReportType.TRIP_COSTS,
              },
              {
                name: 'printout.timesheetWorkCosts',
                type: ReportType.TIMESHEET_WORK_COSTS,
              },
              {
                name: 'printout.driverWorkingDays',
                type: ReportType.DRIVER_WORKING_DAYS,
              },
              {
                name: 'printout.driverCompleteSettlement',
                type: ReportType.DRIVER_COMPLETE_SETTLEMENT,
              },
              {
                name: 'printout.nightWorkDutyDelegationsReport',
                type: ReportType.NIGHT_WORK_DUTY_DELEGATIONS_REPORT,
              },
              {
                name: 'printout.print-leave-equivalent-calculation',
                type: ReportType.TIMESHEET_LEAVE_EQUIVALENT_CALCULATION,
              },
            ],
          },
          {
            group: 'printout.statementsAndForms',
            type: ReportGroupType.StatementsAndForms,
            reports: [
              {
                name: 'printout.driverInstruction',
                type: ReportType.DRIVER_INSTRUCTION,
              },
              {
                name: 'printout.outgoingChecklist',
                type: ReportType.OUTGOING_CHECKLIST,
              },
              {
                name: 'printout.driverCardApplication',
                type: ReportType.DRIVER_CARD_APPLICATION,
              },
              {
                name: 'printout.statementOnEmployeesCount',
                type: ReportType.STATEMENT_ON_EMPLOYEES_COUNT,
              },
              {
                name: 'printout.declarationNotReturnToPlaceOfResidence',
                type: ReportType.DECLARATION_NOT_RETURN_TO_PLACE_OF_RESIDENCE,
              },
            ],
          },
        ]

      case 2:
        return [
          {
            group: 'printout.workTrips',
            type: ReportGroupType.WorkTrips,
            reports: [
              {
                name: 'printout.tripSummary',
                type: ReportType.TRIP_SUMMARY,
              },
              {
                name: 'printout.tripBasics',
                type: ReportType.BASIC_TRIP_PRINT,
              },
              {
                name: 'printout.groupedTripBasics',
                type: ReportType.GROUPED_TRIP_BASICS,
              },
              {
                name: 'printout.tripGeneralStatement',
                type: ReportType.TRIP_GENERAL_STATEMENT,
              },
              {
                name: 'printout.tripDetails',
                type: ReportType.TRIP_DETAILS,
              },
              {
                name: 'printout.tripInvoices',
                type: ReportType.TRIP_INVOICES,
              },
              {
                name: 'printout.businessTripCommand',
                type: ReportType.BUSINESS_TRIP_COMMAND,
              },
              {
                name: 'printout.tripDepositRequest',
                type: ReportType.TRIP_DEPOSIT_REQUEST,
              },
              {
                name: 'printout.tripForeignCountry',
                type: ReportType.TRIP_FOREIGN_COUNTRY,
              },
              {
                name: 'printout.foreignDeclarations',
                type: ReportType.FOREIGN_COUNTRY_DECLARATIONS_DEADLINES,
              },
              {
                name: 'printout.foreignAllowances',
                type: ReportType.FOREIGN_ALLOWANCES,
              },
            ],
          },
          {
            group: 'printout.managerReports',
            type: ReportGroupType.ManagerReports,
            reports: [
              {
                name: 'printout.managerReport1',
                type: ReportType.MANAGER_REPORT_1,
              },
              {
                name: 'printout.managerReport2',
                type: ReportType.MANAGER_REPORT_2,
              },
              {
                name: 'printout.managerReport3',
                type: ReportType.MANAGER_REPORT_3,
              },
            ],
          },
          {
            group: 'printout.dataAccessEventReports',
            type: ReportGroupType.DataAccessEvents,
            reports: [
              {
                name: 'printout.dataAccessEvents',
                type: ReportType.DATA_ACCESS_EVENTS,
              },
            ],
          },
          ...(showServiceReports
            ? [
                {
                  group: 'printout.service',
                  type: ReportGroupType.ServiceReports,
                  reports: [
                    {
                      name: 'printout.cardReadingDeadlineService',
                      type: ReportType.CARD_READING_DEADLINE_SERVICE,
                    },
                    {
                      name: 'printout.foreignDeclarations',
                      type: ReportType.FOREIGN_COUNTRY_SERVICE_DECLARATIONS_DEADLINES,
                    },
                    {
                      name: 'printout.freeDaysSummaryService',
                      type: ReportType.FREE_DAYS_SUMMARY_SERVICE,
                    },
                    {
                      name: 'printout.minimalPaymentCountryHoursService',
                      type: ReportType.MINIMAL_PAYMENT_COUNTRY_HOURS_SERVICE,
                    },
                    {
                      name: 'printout.minimalPaymentCountryAmountsService',
                      type: ReportType.MINIMAL_PAYMENT_COUNTRY_AMOUNTS_SERVICE,
                    },
                    {
                      name: 'printout.timesheetHoursService',
                      type: ReportType.TIMESHEET_HOURS_SERVICE,
                    },
                    {
                      name: 'printout.foreignTripsService',
                      type: ReportType.FOREIGN_TRIPS_SERVICE,
                    },
                  ],
                },
              ]
            : []),
        ]

      case 3:
        return [
          {
            group: 'printout.dataCorrectnessControl',
            type: ReportGroupType.VehicleDataCorrectnessControl,
            reports: [
              {
                name: 'printout.vehicleActivities',
                type: ReportType.VEHICLE_ACTIVITIES,
              },
              {
                name: 'printout.vehicleDataCompletness',
                type: ReportType.VEHICLE_DATA_COMPLETNESS,
              },
              {
                name: 'printout.vehicleCardReadingDeadline',
                type: ReportType.VEHICLE_CARD_READING_DEADLINE,
              },
              {
                name: 'printout.vehicleCalibrationDeadline',
                type: ReportType.VEHICLE_CALIBRATION_DEADLINE,
              },
              {
                name: 'printout.vehicleCardReadingAndCalibrationDeadline',
                type: ReportType.VEHICLE_CARD_READING_AND_CALIBRATION_DEADLINE,
              },
              {
                name: 'printout.vehicleDayDetails',
                type: ReportType.VEHICLE_DAY_DETAILS,
              },
              {
                name: 'printout.vehicleFaultsAndEvents',
                type: ReportType.VEHICLE_FAULTS_AND_EVENTS,
              },
              {
                name: 'printout.vehicleCardInsertionWithdrawal',
                type: ReportType.VEHICLE_CARD_INSERTION_WITHDRAWAL,
              },
              {
                name: 'printout.vehicleTachographDeadline',
                type: ReportType.VEHICLE_TACHOGRAPH_DEADLINE,
              },
              {
                name: 'printout.vehicleTachoDetails',
                type: ReportType.VEHICLE_TACHO_DETAILS,
              },
            ],
          },
        ]
    }

    return []
  }

  const checkReportAccess = (type: ReportType): boolean => {
    let result = true

    switch (type) {
      case ReportType.DRIVER_CALENDAR:
      case ReportType.LOCALIZATION:
      case ReportType.DRIVER_DAY_DETAILS:
      case ReportType.DRIVER_PERIOD_DISTANCE:
      case ReportType.WEEKLY_RESTS:
      case ReportType.WEEKLY_RESTS_WITH_LOCATION:
      case ReportType.TIMESHEET_TIME_SUMMARY:
      case ReportType.DRIVER_AVAILABLE_TIME:
      case ReportType.COMPANY_REPUTATION_INDEX:
        if (isDriver(companyId)) result = false
        break
    }

    return result
  }

  const checkAccess = (
    type: ReportGroupType,
    rolesInCompany: Array<RolePermission>,
  ): boolean => {
    if (isAdmin() || isCompanyAdmin() || isPaymentAdmin() || isDemoCompanyAdmin()) return true

    let result = true

    switch (type) {
      case ReportGroupType.DataCorrectnessControl:
        if (
          (!rolesInCompany.some(
            (r) => r.canViewActivities || r.canEditActivities,
          ) &&
            !rolesInCompany.some(
              (r) => r.canViewDelegations || r.canEditDelegations,
            ) &&
            !rolesInCompany.some(
              (r) => r.canViewBillingData || r.canEditBillingData,
            )) ||
          isDriver(companyId)
        )
          result = false
        break
      case ReportGroupType.VehicleDataCorrectnessControl:
        if (
          !rolesInCompany.some(
            (r) => r.canViewActivities || r.canEditActivities,
          ) ||
          isDriver(companyId)
        )
          result = false
        break
      case ReportGroupType.ItdControlAndAnalysis:
        if (
          !rolesInCompany.some(
            (r) => r.canViewActivities || r.canEditActivities,
          )
        )
          result = false
        break
      case ReportGroupType.StatementsAndForms:
        if (
          !rolesInCompany.some(
            (r) => r.canViewActivities || r.canEditActivities,
          ) ||
          isDriver(companyId)
        )
          result = false
        break
      case ReportGroupType.PipControl:
        if (
          !rolesInCompany.some(
            (r) => r.canViewBillingData || r.canEditBillingData,
          )
        )
          result = false
        break
      case ReportGroupType.WorkTrips:
        if (
          !rolesInCompany.some(
            (r) => r.canViewDelegations || r.canEditDelegations,
          )
        )
          result = false
        break
      case ReportGroupType.ManagerReports:
        if (!rolesInCompany.some((r) => r.canEditBillingData)) result = false
        break
      case ReportGroupType.DataAccessEvents:
        if (!rolesInCompany.some((r) => r.canEditBillingData)) result = false
        break
      case ReportGroupType.ServiceReports:
        if (
          !rolesInCompany.some(
            (r) => r.canViewBillingData || r.canEditBillingData,
          )
        )
          result = false
        break
    }

    return result
  }

  const getCheckedDrivers = (checkedDrivers: ReadonlyArray<string>) => {
    let drivers = []

    const driver = isDriver(companyId)
    if (driver) {
      // For driver users, we'd need driverId from route params
      // For now, return empty array
      drivers = []
    } else if (checkedDrivers != null && checkedDrivers.length > 0) {
      drivers = [...checkedDrivers]
    }

    return drivers
  }

  const isReportEnabled = (
    report: ReportItem,
    groupType: ReportGroupType,
  ): boolean => {
    const drivers = getCheckedDrivers(checkedDrivers)

    return (
      ((activeItem === ActiveMenuItem.PRINTOUT_DRIVER &&
        (drivers.length > 0 || allowsNoSelection(report, groupType))) ||
        (activeItem === ActiveMenuItem.PRINTOUT_VEHICLE &&
          checkedVehicles.length > 0)) &&
      !report.disabled
    )
  }

  const canMultiSelect = (report: ReportItem): boolean =>
    !NON_MULTI_SELECT_REPORTS.has(report.type)


  const filterReportsBySearch = (
    reports: ReportItem[],
    groupKey: string,
  ): ReportItem[] => {
    if (!isSearching) return reports
    if (normalizeForSearch(t(groupKey)).includes(normalizedSearch)) {
      return reports
    }
    return reports.filter((report) =>
      normalizeForSearch(t(report.name)).includes(normalizedSearch),
    )
  }

  const toggleReportsSelection = (reports: ReportItem[], select: boolean) => {
    if (onToggleReports) {
      onToggleReports(reports, select)
      return
    }
    reports.forEach((report) => {
      if (selectedReports?.has(report.type) !== select) onToggleReport?.(report)
    })
  }

  const handleReportClick = (
    report: ReportItem,
    groupType: ReportGroupType,
  ) => {
    const drivers = getCheckedDrivers(checkedDrivers)

    if (
      (activeItem === ActiveMenuItem.PRINTOUT_DRIVER &&
        (drivers.length > 0 || allowsNoSelection(report, groupType))) ||
      (activeItem === ActiveMenuItem.PRINTOUT_VEHICLE &&
        checkedVehicles.length > 0 &&
        !report.disabled)
    ) {
      const rateRangeInDays = getDifferenceBetweenDatesInDays(
        moment(dateFrom),
        moment(dateTo),
      )
      if (rateRangeInDays !== '-' && rateRangeInDays > 365) {
        onDateRangeWarning()
      } else {
        onReportClick(report)
      }
    }
  }

  // Jak SelectedItem w WPF: wybrany wiersz pokazuje opcje raportu i dolacza go
  // do zaznaczonych. Raporty spoza wydruku grupowego drukuja sie od razu.
  const handleRowClick = (report: ReportItem, groupType: ReportGroupType) => {
    if (!isSelectable || !canMultiSelect(report)) {
      handleReportClick(report, groupType)
      return
    }

    if (openedReport === report.type) {
      setOpenedReport(null)
      return
    }

    if (!selectedReports?.has(report.type)) onToggleReport?.(report)
    setOpenedReport(report.type)
  }

  const renderCategoryCard = (g: PrintoutGroup, index: number) => {
    const canAccess = checkAccess(g.type, permissions)
    if (!canAccess) return null

    const visibleReports = showOnlyFavorites
      ? g.reports.filter((report) => activeSet.has(report.type))
      : g.reports

    if (showOnlyFavorites && visibleReports.length === 0) return null

    const filteredReports = filterReportsBySearch(visibleReports, g.group)

    if (isSearching && filteredReports.length === 0) return null

    const isExpanded = isSearching || expandedCategories.has(index)

    const selectableReports = isSelectable
      ? filteredReports.filter(
          (report) =>
            canMultiSelect(report) &&
            checkReportAccess(report.type) &&
            isReportEnabled(report, g.type),
        )
      : []
    const selectedInGroup = selectableReports.filter((report) =>
      selectedReports?.has(report.type),
    ).length
    const allSelectedInGroup =
      selectableReports.length > 0 && selectedInGroup === selectableReports.length

    return (
      <div
        key={index}
        className={`tw-flex tw-flex-col tw-border tw-border-secondary tw-bg-white`}
      >
        <div
          onClick={() => toggleCategory(index)}
          className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-b-2 tw-border-secondary tw-bg-[#DFE7FA] tw-p-3 tw-transition-colors hover:tw-bg-[#D2DCF7]"
        >
          {isSelectable && (
            <Checkbox
              checked={allSelectedInGroup}
              indeterminate={selectedInGroup > 0 && !allSelectedInGroup}
              disabled={selectableReports.length === 0}
              onChange={() => {}}
              onClick={(e) => {
                e.stopPropagation()
                if (selectableReports.length === 0) return
                toggleReportsSelection(selectableReports, !allSelectedInGroup)
              }}
              className="tw-h-4 tw-w-4 tw-flex-shrink-0"
            />
          )}
          <FaIcon
            icon={faUsers}
            className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-content-base"
          />
          <span className="tw-flex-1 tw-break-words tw-text-xs tw-font-semibold tw-text-content-base">
            {highlightSearchMatch(t(g.group), normalizedSearch)}
          </span>
          <FaIcon
            icon={isExpanded ? faChevronUp : faChevronDown}
            className="tw-h-3 tw-w-3 tw-flex-shrink-0 tw-text-content-base"
          />
        </div>

        {isExpanded && (
          <div className="tw-flex tw-min-h-fit tw-flex-col">
            {filteredReports.map((report, reportIndex) => {
              const canAccessReport = checkReportAccess(report.type)
              if (!canAccessReport) return null

              const isEnabled = isReportEnabled(report, g.type)
              const isSelected = selectedReports?.has(report.type) ?? false
              const isGenerated = generatedReportTypes.has(report.type)
              const isLastGenerated = lastGeneratedReportTypes.has(report.type)
              const isFavourite = favorites.has(report.type)
              const isShipping = shipping.has(report.type)
              const showFavoriteToggle =
                !showOnlyFavorites || listType === 'favorites'
              const showShippingToggle =
                !showOnlyFavorites || listType === 'shipping'

              const isOpened = isEnabled && openedReport === report.type

              return (
                <React.Fragment key={reportIndex}>
                <div
                  onClick={() => isEnabled && handleRowClick(report, g.type)}
                  title={
                    isLastGenerated
                      ? t('printout.last-generated-report')
                      : isGenerated
                        ? t('printout.generated-in-session')
                        : undefined
                  }
                  className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-b tw-border-secondary/40 tw-p-3 tw-transition-all last:tw-border-b-0 ${
                    isLastGenerated
                      ? 'tw-bg-primary/15'
                      : isGenerated
                        ? 'tw-bg-primary/[0.07]'
                        : isOpened
                          ? 'tw-bg-primary/5'
                          : 'tw-bg-white'
                  } ${
                    isEnabled
                      ? isGenerated
                        ? 'hover:tw-bg-primary/25'
                        : 'hover:tw-bg-primary/5'
                      : 'tw-cursor-not-allowed tw-opacity-30'
                  }`}
                >
                  {isSelectable && (
                    <Checkbox
                      checked={isSelected}
                      disabled={!isEnabled || !canMultiSelect(report)}
                      onChange={() => {}}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (isEnabled && canMultiSelect(report)) {
                          onToggleReport?.(report)
                        }
                      }}
                      className="tw-h-3.5 tw-w-3.5 tw-flex-shrink-0"
                    />
                  )}
                  <FaIcon
                    icon={faPrint}
                    className="tw-h-3 tw-w-3 tw-flex-shrink-0 tw-text-content-base"
                    title={t('operation.get-pdf')}
                  />
                  <span
                    className={`tw-flex-1 tw-break-words tw-text-left tw-text-xs ${
                      isLastGenerated ? 'tw-font-semibold' : 'tw-font-normal'
                    } ${
                      report.disabled ? 'tw-opacity-30' : 'tw-text-content-base'
                    }`}
                  >
                    {highlightSearchMatch(t(report.name), normalizedSearch)}
                    {report.type === ReportType.DRIVER_CARD_READING &&
                      singleSelectedDriver &&
                      ` - ${singleSelectedDriver.fullName}`}
                  </span>
                  {isGenerated && (
                    <FaIcon
                      icon={faCircleCheck}
                      className={`tw-h-3.5 tw-w-3.5 tw-flex-shrink-0 ${
                        isLastGenerated
                          ? 'tw-text-primary'
                          : 'tw-text-primary/50'
                      }`}
                      title={t(
                        isLastGenerated
                          ? 'printout.last-generated-report'
                          : 'printout.generated-in-session',
                      )}
                    />
                  )}
                  {showFavoriteToggle && (
                    <EtBrandSygnet
                      monochrome={!isFavourite}
                      className={`tw-h-5 tw-w-5 tw-flex-shrink-0 tw-cursor-pointer tw-transition-colors`}
                      onClick={(e) => toggleFavorite(report.type, e)}
                    />
                  )}
                  {showShippingToggle && (
                    <FaIcon
                      icon={isShipping ? faPaperPlaneSolid : faPaperPlaneRegular}
                      className={`tw-h-4 tw-w-4 tw-flex-shrink-0 tw-cursor-pointer tw-transition-colors ${
                        isShipping
                          ? 'tw-text-primary'
                          : 'tw-text-content-base/60'
                      }`}
                      onClick={(e) => toggleShipping(report.type, e)}
                    />
                  )}
                  {isSelectable && canMultiSelect(report) && (
                    <FaIcon
                      icon={isOpened ? faChevronUp : faChevronDown}
                      className="tw-h-3 tw-w-3 tw-flex-shrink-0 tw-text-content-base/60"
                    />
                  )}
                </div>
                {isOpened && (
                  <ReportParamsPanel report={report.type} companyId={companyId} />
                )}
                </React.Fragment>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // Check if user has any permissions
  const hasPermissions = permissions.some(
    (p) =>
      p.canViewActivities ||
      p.canEditActivities ||
      p.canViewDelegations ||
      p.canEditDelegations ||
      p.canViewBillingData ||
      p.canEditBillingData,
  )

  // if (!hasPermissions) {
  //   return (
  //     <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
  //       <p className="tw-text-content-base">
  //         {t('printout.no-permissions-to-generate-printouts')}
  //       </p>
  //     </div>
  //   )
  // }

  // Collect all printout groups into a single array
  const allPrintoutGroups = React.useMemo((): PrintoutGroup[] => {
    const definitions =
      activeItem === ActiveMenuItem.PRINTOUT_DRIVER
        ? [...getPrintoutGroup(0), ...getPrintoutGroup(1), ...getPrintoutGroup(2)]
        : activeItem === ActiveMenuItem.PRINTOUT_VEHICLE
          ? getPrintoutGroup(3)
          : []

    // Dostepnosc w wybranym formacie bierze sie z capabilities serwera, wiec lista
    // musi sie przeliczyc i po przelaczeniu PDF/CSV, i po doczytaniu capabilities.
    return definitions.map((group) => ({
      ...group,
      reports: group.reports.map((report) => ({
        ...report,
        disabled: !supportsOutputFormat(capabilities, report.type, outputFormat),
      })),
    }))
  }, [activeItem, outputFormat, capabilities])

  // Zaznaczenie przezywa przeladowanie strony, wiec po zmianie formatu wydruku
  // albo trybu (kierowcy/pojazdy) odrzucamy raporty, ktorych w tym ukladzie nie
  // da sie wygenerowac. Brak zaznaczonych kierowcow selekcji nie czysci, tak samo
  // jak samo wejscie na strone - przy pierwszym renderze uprawnienia moga byc
  // jeszcze niezaladowane i skasowalyby zapamietane zaznaczenie.
  const pruneKeyRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    const pruneKey = `${activeItem}|${outputFormat}|${listType}|${activeSet.size}`
    const previousKey = pruneKeyRef.current
    pruneKeyRef.current = pruneKey

    if (previousKey === null || previousKey === pruneKey) return
    if (!isSelectable || !selectedReports || selectedReports.size === 0) return

    // Dopoki nie znamy uprawnien, kazda grupa wygladalaby na niedostepna.
    const permissionsKnown =
      isAdmin() ||
      isCompanyAdmin() ||
      isPaymentAdmin() ||
      isDemoCompanyAdmin() ||
      permissions.length > 0
    if (!permissionsKnown) return

    const available = new Set<ReportType>()

    allPrintoutGroups.forEach((g) => {
      if (!checkAccess(g.type, permissions)) return
      g.reports.forEach((report) => {
        const visible = !showOnlyFavorites || activeSet.has(report.type)
        if (
          visible &&
          !report.disabled &&
          canMultiSelect(report) &&
          checkReportAccess(report.type)
        ) {
          available.add(report.type)
        }
      })
    })

    // Wszystko, czego w biezacym ukladzie nie ma na liscie, wypada z zaznaczenia
    // - inaczej raport kierowcy zostalby wygenerowany w trybie pojazdow albo
    // niewidoczny raport trafilby do paczki.
    const toDrop = Array.from(selectedReports)
      .filter((type) => !available.has(type))
      .map((type) => ({ name: '', type, disabled: false }))

    if (toDrop.length > 0) toggleReportsSelection(toDrop, false)
  }, [outputFormat, activeItem, listType, activeSet])

  React.useEffect(() => {
    if (showOnlyFavorites && activeSet.size > 0) {
      const allIndices = allPrintoutGroups
        .map((_, index) => index)
        .filter((index) => {
          const group = allPrintoutGroups[index]
          const hasFilteredReports = group.reports.some((report) =>
            activeSet.has(report.type),
          )
          return hasFilteredReports && checkAccess(group.type, permissions)
        })
      setExpandedCategories(new Set(allIndices))
    }
  }, [showOnlyFavorites, activeSet.size, allPrintoutGroups.length, listType])

  const hasVisibleReports = allPrintoutGroups.some((group) => {
    if (!checkAccess(group.type, permissions)) return false
    return group.reports.some(
      (report) => activeSet.has(report.type) && checkReportAccess(report.type),
    )
  })

  const searchResultCount = isSearching
    ? allPrintoutGroups.reduce((sum, group) => {
        if (!checkAccess(group.type, permissions)) return sum
        const visible = showOnlyFavorites
          ? group.reports.filter((report) => activeSet.has(report.type))
          : group.reports
        return (
          sum +
          filterReportsBySearch(visible, group.group).filter((report) =>
            checkReportAccess(report.type),
          ).length
        )
      }, 0)
    : 0

  const emptyTitleKey =
    listType === 'shipping'
      ? 'printout.no-shipping-printouts'
      : 'printout.no-favorite-printouts'
  const emptyHintKey =
    listType === 'shipping'
      ? 'printout.add-shipping-printouts-hint'
      : 'printout.add-favorite-printouts-hint'

  const showNoSearchResults = isSearching && searchResultCount === 0

  return (
    <div className="tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col">
      <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2 tw-border-b tw-border-secondary tw-bg-white tw-px-3 tw-py-2">
        <FaIcon
          icon={faMagnifyingGlassSolid}
          className="tw-h-3.5 tw-w-3.5 tw-flex-shrink-0 tw-text-content-base/60"
        />
        <div className="tw-w-full tw-max-w-[360px]">
          <TextField
            fullWidth
            type="text"
            id="printout-report-search"
            autoComplete="off"
            placeholder={t('printout.search-report-placeholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        {isSearching && (
          <span className="tw-flex-shrink-0 tw-text-xs tw-text-content-base/70">
            {searchResultCount}
          </span>
        )}
        {searchInput !== '' && (
          <button
            type="button"
            onClick={() => setSearchInput('')}
            title={t('operation.clear')}
            className="tw-flex tw-h-6 tw-w-6 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded tw-text-content-base/60 tw-transition-colors hover:tw-bg-base-200 hover:tw-text-content-base"
          >
            <FaIcon icon={faXmark} className="tw-h-3.5 tw-w-3.5" />
          </button>
        )}
      </div>

      <div className="tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col tw-overflow-y-auto">
        {showOnlyFavorites && isLoading ? (
          <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-p-12">
            <LoaderIcon className="tw-w-16 tw-animate-spin" />
          </div>
        ) : showOnlyFavorites && !hasVisibleReports ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-12 tw-text-center">
            {listType === 'shipping' ? (
              <FaIcon
                icon={faPaperPlaneRegular}
                className="tw-mb-4 tw-h-16 tw-w-16 tw-text-gray-300"
              />
            ) : (
              <EtBrandSygnet
                monochrome
                className="tw-mb-4 tw-h-16 tw-w-16 tw-text-gray-300"
              />
            )}
            <h3 className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-content-base">
              {t(emptyTitleKey)}
            </h3>
            <p className="tw-text-sm tw-text-content-base/80">
              {t(emptyHintKey)}
            </p>
          </div>
        ) : showNoSearchResults ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-12 tw-text-center">
            <FaIcon
              icon={faMagnifyingGlassSolid}
              className="tw-mb-4 tw-h-12 tw-w-12 tw-text-gray-300"
            />
            <p className="tw-text-sm tw-text-content-base/80">
              {t('printout.no-search-results')}
            </p>
          </div>
        ) : (
          allPrintoutGroups.map((g, i) => renderCategoryCard(g, i))
        )}
      </div>
    </div>
  )
}

export default PrintoutGroupsList
